import React, {useEffect, useMemo, useState} from "react";
import {Button, Form, Spinner} from "react-bootstrap";
import {ToastContainer} from "react-toastify";
import AppComponentCardHeader from "../common/AppComponentCardHeader";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useAuth from "../../../hooks/useAuth";
import {toastError, toastSuccess} from "../../../helpers/Toast";
import {normalizeRoles, ROLES} from "../../../auth/roles";
import "../../../css/UserAccount.css";

const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{8,24}$/;

const ROLE_LABELS = {
  ADMIN: "Administrator",
  MANAGER: "Kierownik",
  HR: "Kadry / HR",
  EMPLOYEE: "Pracownik",
  USER: "Użytkownik",
  GUEST: "Gość",
  MINION: "Pracownik pomocniczy"
};

const UserAccount = () => {
  const axiosPrivate = useAxiosPrivate();
  const {auth, setAuth} = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [account, setAccount] = useState(null);
  const [form, setForm] = useState({firstName: "", lastName: "", phoneNumber: ""});
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");

  const roles = useMemo(() => normalizeRoles(auth?.roles), [auth?.roles]);
  const minion = roles.includes(ROLES.MINION);

  const errorMessage = error => {
    const data = error?.response?.data;
    if (typeof data === "string") return data;
    return data?.message || data?.error_message || data?.error || "Nie udało się wykonać operacji.";
  };

  const loadAccount = async () => {
    if (minion) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axiosPrivate.get("/account/me");
      const data = response.data || {};
      setAccount(data);
      setForm({
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        phoneNumber: data.phoneNumber || ""
      });
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccount();
  }, [minion]);

  const saveProfile = async event => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await axiosPrivate.put("/account/me", form);
      const data = response.data || {};
      setAccount(data);
      const displayName = `${data.firstName || ""} ${data.lastName || ""}`.trim() || data.login || auth?.user;
      setAuth(current => ({...current, displayName}));
      toastSuccess("Dane konta zostały zapisane.");
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async event => {
    event.preventDefault();
    if (!oldPassword) {
      toastError("Podaj stare hasło.");
      return;
    }
    if (!PWD_REGEX.test(newPassword)) {
      toastError("Hasło musi mieć 8–24 znaki, małą i wielką literę, cyfrę oraz znak ! @ # $ %.");
      return;
    }
    if (newPassword !== passwordConfirmation) {
      toastError("Podane nowe hasła są różne.");
      return;
    }

    setSavingPassword(true);
    try {
      await axiosPrivate.put("/account/me/password", {oldPassword, newPassword});
      setOldPassword("");
      setNewPassword("");
      setPasswordConfirmation("");
      toastSuccess("Hasło zostało zmienione.");
    } catch (error) {
      const errorData = error?.response?.data;
      if (error?.response?.status === 422 && (errorData === "Old password is invalid" || errorData?.message === "Old password is invalid")) {
        toastError("Stare hasło jest nieprawidłowe.");
      } else {
        toastError(errorMessage(error));
      }
    } finally {
      setSavingPassword(false);
    }
  };

  if (minion) {
    return (
      <div id="layoutSidenav_content" className="user-account-page">
        <AppComponentCardHeader title="Moje konto"/>
        <div className="container-fluid px-4 py-3">
          <div className="user-account-denied">Rola Pracownik pomocniczy nie ma dostępu do panelu własnego konta.</div>
        </div>
      </div>
    );
  }

  return (
    <div id="layoutSidenav_content" className="user-account-page">
      <ToastContainer/>
      <AppComponentCardHeader title="Moje konto"/>
      <div className="container-fluid px-4 py-3">
        {loading ? (
          <div className="user-account-loading"><Spinner animation="border" size="sm"/> Pobieranie danych…</div>
        ) : account ? (
          <div className="user-account-grid">
            <section className="card shadow-sm user-account-card">
              <div className="user-account-card-header">
                <div>
                  <span>Konto Qstaff</span>
                  <h5>Dane podstawowe</h5>
                </div>
                <div className="user-account-login">{account.login}</div>
              </div>
              <form onSubmit={saveProfile} className="user-account-form">
                <Form.Group>
                  <Form.Label>Login</Form.Label>
                  <Form.Control value={account.login || ""} disabled/>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Imię</Form.Label>
                  <Form.Control required value={form.firstName} onChange={event => setForm(value => ({...value, firstName: event.target.value}))}/>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Nazwisko</Form.Label>
                  <Form.Control required value={form.lastName} onChange={event => setForm(value => ({...value, lastName: event.target.value}))}/>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Telefon</Form.Label>
                  <Form.Control value={form.phoneNumber} onChange={event => setForm(value => ({...value, phoneNumber: event.target.value}))}/>
                </Form.Group>
                <div className="user-account-role-row">
                  <span>Role</span>
                  <div>
                    {(account.roles || []).map(role => <strong key={role}>{ROLE_LABELS[role] || role}</strong>)}
                  </div>
                </div>
                {account.linkedEmployee && (
                  <div className="user-account-note">Konto jest powiązane z kartoteką pracownika #{account.employeeId}. Zmiana imienia, nazwiska lub telefonu jest zapisywana również w kartotece.</div>
                )}
                <div className="user-account-actions">
                  <Button type="submit" disabled={saving}>{saving ? "Zapisywanie…" : "Zapisz dane"}</Button>
                </div>
              </form>
            </section>

            <section className="card shadow-sm user-account-card">
              <div className="user-account-card-header">
                <div>
                  <span>Bezpieczeństwo</span>
                  <h5>Zmiana hasła</h5>
                </div>
              </div>
              <form onSubmit={savePassword} className="user-account-form">
                <Form.Group>
                  <Form.Label>Stare hasło</Form.Label>
                  <Form.Control type="password" autoComplete="current-password" value={oldPassword} onChange={event => setOldPassword(event.target.value)}/>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Nowe hasło</Form.Label>
                  <Form.Control type="password" autoComplete="new-password" value={newPassword} onChange={event => setNewPassword(event.target.value)}/>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Powtórz nowe hasło</Form.Label>
                  <Form.Control type="password" autoComplete="new-password" value={passwordConfirmation} onChange={event => setPasswordConfirmation(event.target.value)}/>
                </Form.Group>
                <small>8–24 znaki, mała i wielka litera, cyfra oraz jeden ze znaków ! @ # $ %.</small>
                <div className="user-account-actions">
                  <Button type="submit" disabled={savingPassword}>{savingPassword ? "Zapisywanie…" : "Zmień hasło"}</Button>
                </div>
              </form>
            </section>
          </div>
        ) : (
          <div className="user-account-denied">Nie udało się pobrać danych konta.</div>
        )}
      </div>
    </div>
  );
};

export default UserAccount;
